import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { SheetClose } from '@/components/ui/sheet';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { exerciseFromVariant, loadCatalogue, resolveProgram } from '../../data/catalogue';
import { MAX_EXERCISES } from '../../domain/constants';
import { totalPoints } from '../../domain/workspace';
import { programLimits } from '../../domain/rules';
import { deNumber, parseDeNumber } from '../../lib/format';
import { t } from '../../i18n';
import { figureGroupTitle, figureVariantNames } from '../../i18n/labels';
import { useLang } from '../hooks/useLang';
import { useToast } from '../components/Toast';
import { wosGlyph } from '../wos';
import type { Catalogue, CatalogueKey, Exercise, Figure, FigureVariant, Programme } from '../../types';

const ALL_GROUPS = ' all';

/** Cap the rendered rows; the catalogues hold up to 553 variants. */
const MAX_ROWS = 320;

export interface FigurePickerHandle {
  open(programme: Programme, key: CatalogueKey, onAdd: (ex: Exercise) => void): Promise<void>;
  close(): void;
}

function limitFor(p: Programme): number {
  return programLimits(p)?.maxTotal ?? MAX_EXERCISES;
}

interface Row {
  variant: FigureVariant;
  used: boolean;
}

/** Every language's name, regardless of the current UI language — so
 *  searching still matches a term in a language other than the one being
 *  displayed right now (see `figureVariantNames()`, `src/i18n/labels.ts`,
 *  for what's actually *shown*). */
function searchHaystack(v: FigureVariant, fig: Figure): string {
  const variantNames = Object.values(v.i18n).join(' ');
  const figureNames = Object.values(fig.i18n)
    .map((tr) => tr.name)
    .join(' ');
  return `${v.id} ${variantNames} ${figureNames}`.toLowerCase();
}
interface Section {
  key: number;
  label: string;
  rows: Row[];
}

/**
 * The figure picker sheet — a persistent singleton, mounted once in
 * `AppShell.tsx` and opened imperatively via a ref handle from
 * `ProgrammView.tsx`, since it's triggered from that view's "Add exercise"
 * button rather than from parent-owned open/closed state.
 *
 * Rows are filtered directly off the live `query`/`min`/`max`/`group` state
 * via `useMemo` — React's reconciliation re-renders the (at most
 * `MAX_ROWS`-capped) list cheaply enough that no debounce is needed.
 */
export const FigurePicker = forwardRef<FigurePickerHandle>((_props, ref) => {
  const lang = useLang();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [programme, setProgramme] = useState<Programme | null>(null);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const onAddRef = useRef<((ex: Exercise) => void) | null>(null);

  const [query, setQuery] = useState('');
  const [group, setGroup] = useState<number | null>(null);
  const [min, setMin] = useState<number>(Number.NaN);
  const [max, setMax] = useState<number>(Number.NaN);
  const [activeIndex, setActiveIndex] = useState(-1);
  // `programme.program` is mutated in place by `onAdd` (same mutable-object
  // model as `WorkspaceStore`), so this version tick is what tells the
  // `rows` memo below to recompute after every add — the `programme`
  // object's own reference never changes.
  const [progVersion, setProgVersion] = useState(0);

  const searchRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useImperativeHandle(
    ref,
    () => ({
      async open(p, key, add) {
        setProgramme(p);
        onAddRef.current = add;
        setQuery('');
        setGroup(null);
        setMin(Number.NaN);
        setMax(Number.NaN);
        setCatalogue(null);
        setLoadError(null);
        setOpen(true);
        try {
          setCatalogue(await loadCatalogue(key));
        } catch (err) {
          setLoadError(err instanceof Error ? err.message : t('picker.loadFailed'));
        }
      },
      close() {
        setOpen(false);
      },
    }),
    [],
  );

  // Desktop-only auto-focus — deliberately skipped on mobile to avoid
  // popping the on-screen keyboard as soon as the sheet opens.
  useEffect(() => {
    if (!open) return;
    if (!window.matchMedia('(min-width: 760px)').matches) return;
    const timer = setTimeout(() => searchRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [open]);

  const { sections, flatRows, shown, full, limit } = useMemo(() => {
    if (!catalogue || !programme) return { sections: [] as Section[], flatRows: [] as FigureVariant[], shown: 0, full: false, limit: 0 };
    const used = new Set(programme.program.map((u) => u.nr));
    const lim = limitFor(programme);
    const isFull = programme.program.length >= lim;
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

    const outSections: Section[] = [];
    const outFlat: FigureVariant[] = [];
    let count = 0;

    catalogue.groups.forEach((g, gi) => {
      if (group !== null && group !== gi) return;
      const rows: Row[] = [];
      for (const fig of catalogue.figures) {
        if (fig.group !== gi) continue;
        for (const v of fig.variants) {
          if (!Number.isNaN(min) && v.points < min) continue;
          if (!Number.isNaN(max) && v.points > max) continue;
          if (terms.length > 0) {
            const hay = searchHaystack(v, fig);
            if (!terms.every((term) => hay.includes(term))) continue;
          }
          if (count >= MAX_ROWS) break;
          count++;
          rows.push({ variant: v, used: used.has(v.id) });
          outFlat.push(v);
        }
      }
      if (rows.length > 0) outSections.push({ key: gi, label: `${g.article} · ${figureGroupTitle(g)}`, rows });
    });

    return { sections: outSections, flatRows: outFlat, shown: count, full: isFull, limit: lim };
    // `lang` isn't read directly above, but `figureGroupTitle()` reads it
    // internally (via `t()`) to build each section's label — without it in
    // the deps, switching UI language wouldn't re-run this memo and the
    // section headers would stay in the old language. `progVersion` is a
    // manual invalidation tick, see its declaration above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, programme, query, group, min, max, progVersion, lang]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [sections]);

  useEffect(() => {
    if (activeIndex >= 0) rowRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function handleAdd(v: FigureVariant): void {
    if (!programme || !onAddRef.current) return;
    const lim = limitFor(programme);
    if (programme.program.length >= lim) {
      toast(t('picker.tooMany', { limit: lim }));
      return;
    }
    const ex: Exercise = exerciseFromVariant(v);
    onAddRef.current(ex);
    const resolved = catalogue ? resolveProgram(catalogue, programme.program) : [];
    toast(t('picker.added', { id: v.id, points: deNumber(totalPoints(resolved)) }));
    setProgVersion((v) => v + 1);
    setQuery('');
    searchRef.current?.focus();
  }

  function handleSearchKeyDown(ev: KeyboardEvent<HTMLInputElement>): void {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setActiveIndex((i) => Math.max(0, Math.min(flatRows.length - 1, (i < 0 ? -1 : i) + 1)));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setActiveIndex((i) => (i < 0 ? flatRows.length - 1 : Math.max(0, i - 1)));
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      const v = flatRows[activeIndex >= 0 ? activeIndex : 0];
      // Matches the vanilla row template: only `full` disables a row,
      // "already used" only dims it (`.is-used`), it stays clickable.
      if (v && !full) handleAdd(v);
    }
  }

  function handleReset(): void {
    setQuery('');
    setGroup(null);
    setMin(Number.NaN);
    setMax(Number.NaN);
  }

  const info = full
    ? t('picker.limitReached', { limit })
    : programme
      ? t('picker.info', {
          shown: `${shown}${shown >= MAX_ROWS ? '+' : ''}`,
          have: programme.program.length,
          limit,
          points: deNumber(totalPoints(catalogue ? resolveProgram(catalogue, programme.program) : [])),
        })
      : '';

  let flatIndex = -1;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) setOpen(false);
      }}
    >
      <SheetContent
        side="bottom"
        // Full-screen below the sm breakpoint (matches SIDE_CLASSES' own
        // `sm:mx-auto sm:max-w-2xl sm:rounded-t-lg` cutoff) — with the
        // on-screen keyboard eating a big chunk of a phone's height while
        // searching, the shared bottom-sheet default of a fixed 85vh (empty
        // space above it) left too little room. Desktop/tablet keep that
        // capped, centered look unchanged.
        className="h-dvh gap-0 p-0 pt-[env(safe-area-inset-top)] sm:h-[85vh] sm:pt-0"
        onOpenAutoFocus={(ev) => {
          // Custom desktop-only focus handled by the effect above.
          ev.preventDefault();
        }}
      >
        <SheetHeader className="gap-3">
          <SheetTitle>{t('picker.title')}</SheetTitle>
          <Input
            ref={searchRef}
            placeholder={t('picker.searchPlaceholder')}
            autoComplete="off"
            aria-label={t('picker.searchLabel')}
            value={query}
            onChange={(ev) => setQuery(ev.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Select value={group === null ? ALL_GROUPS : String(group)} onValueChange={(v) => setGroup(v === ALL_GROUPS ? null : Number(v))}>
              <SelectTrigger className="h-8 min-w-40 flex-1 text-xs" aria-label={t('picker.groupLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_GROUPS}>{t('picker.allGroups')}</SelectItem>
                {catalogue?.groups.map((g, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {g.article} · {figureGroupTitle(g)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="h-8 w-16 font-mono text-xs"
              placeholder={t('picker.minPlaceholder')}
              inputMode="decimal"
              aria-label={t('picker.minLabel')}
              value={Number.isNaN(min) ? '' : String(min)}
              onChange={(ev) => setMin(parseDeNumber(ev.target.value))}
            />
            <Input
              className="h-8 w-16 font-mono text-xs"
              placeholder={t('picker.maxPlaceholder')}
              inputMode="decimal"
              aria-label={t('picker.maxLabel')}
              value={Number.isNaN(max) ? '' : String(max)}
              onChange={(ev) => setMax(parseDeNumber(ev.target.value))}
            />
            <Button variant="ghost" size="icon" className="size-8 shrink-0" onClick={handleReset} title={t('picker.reset')} aria-label={t('picker.reset')}>
              <RotateCcwIcon className="size-4" />
            </Button>
          </div>
        </SheetHeader>
        <SheetBody className="pb-1">
          {loadError ? (
            <p className="p-6 text-center text-sm text-destructive">{loadError}</p>
          ) : !catalogue ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('picker.loading')}</p>
          ) : sections.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('picker.noResults')}</p>
          ) : (
            sections.map((section) => (
              <div key={section.key}>
                <p className="sticky top-0 z-10 bg-background px-4 py-1.5 font-mono text-[10px] font-semibold tracking-widest text-muted-foreground uppercase">
                  {section.label}
                </p>
                {section.rows.map(({ variant: v, used }) => {
                  flatIndex++;
                  const idx = flatIndex;
                  const names = figureVariantNames(v);
                  return (
                    <button
                      key={v.id}
                      ref={(el) => {
                        rowRefs.current[idx] = el;
                      }}
                      className={cn(
                        'flex w-full items-start gap-3 border-b px-4 py-2 text-left hover:bg-secondary disabled:opacity-40',
                        used && 'opacity-50',
                        idx === activeIndex && 'bg-secondary',
                      )}
                      disabled={full}
                      onClick={() => handleAdd(v)}
                    >
                      <span className="w-13 shrink-0 pt-0.5 font-mono text-xs font-semibold text-muted-foreground">{v.id}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block break-words text-[13.5px] font-medium">{names.primary}</span>
                        <span className="block break-words text-[11.5px] text-muted-foreground">{names.secondary}</span>
                      </span>
                      {v.tactical ? (
                        <Badge variant="warn" title={t('picker.tacticalTitle')}>
                          T
                        </Badge>
                      ) : null}
                      {v.final ? (
                        <Badge variant="info" title={t('picker.finalTitle')}>
                          S
                        </Badge>
                      ) : null}
                      <span dangerouslySetInnerHTML={{ __html: wosGlyph(v.wos) }} />
                      <span className="shrink-0 font-mono text-[13.5px] font-semibold">{deNumber(v.points)}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </SheetBody>
        <SheetFooter>
          <span className={cn('flex-1 text-xs text-muted-foreground', full && 'font-semibold text-destructive')}>{info}</span>
          <SheetClose asChild>
            <Button size="sm">{t('picker.done')}</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
});
FigurePicker.displayName = 'FigurePicker';
